import { useMutation, useQuery } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import rg4js from "raygun4js";
import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  InfoCircle,
  PencilFill,
  TrashFill,
} from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import type { Definition } from "@/briefpoint-client";
import Button from "@/components/Button";
import ExpandableText from "@/components/ExpandableText/ExpandableText";
import Loading from "@/components/Loading";
import { FormModal } from "@/components/Modals";
import Pagination from "@/components/Pagination/Pagination";
import SearchInput from "@/components/SearchInput";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { useDefinitionsApi } from "@/hooks/useApi";
import { getDefinitionsOptions } from "@/lib/query-options";
import { normalizeError } from "@/lib/utils";
import AddEditDefinition from "./AddEditDefinition";
import styles from "./DefinitionsTable.module.scss";

export default function DefinitionsTable() {
  const definitionsApi = useDefinitionsApi();
  const [definitionToDelete, setDefinitionToDelete] = useState<Definition>();
  const [definitionToEdit, setDefinitionToEdit] = useState<Definition>();
  const [showAddUpdateTerm, setShowAddUpdateTerm] = useState(false);
  const [search, setSearch] = useState("");

  const definitionsQuery = useQuery({
    ...getDefinitionsOptions(definitionsApi),
    select: (data) => data.filter((d) => !d.isDeleted),
  });

  /* delete definition mutation uses an update and sets isDeleted to true */
  const updateDefinitionMutation = useMutation({
    mutationFn: (definition: Definition) =>
      definitionsApi.definitionUpdate({
        definition: { ...definition, isDeleted: true },
      }),
    onSuccess: () => {
      toast.success("Definition deleted successfully");
    },
    onSettled: () => {
      definitionsQuery.refetch();
      setDefinitionToDelete(undefined);
    },
    onError: (error) => {
      toast.error("Failed to delete definition. Please try again later.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
  });

  /* table column definitions */
  const columns: ColumnDef<Definition>[] = [
    {
      header: ({ column }) => {
        return (
          <Button
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Term
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      meta: {
        width: "20%",
      },
      accessorKey: "term",
      accessorFn: (row) => row.term,
      cell: ({ row }) => {
        return <div className={styles.cellData}>{row.original.term}</div>;
      },
      sortingFn: "text",
    },
    {
      header: ({ column }) => {
        return (
          <Button
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Aliases
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      accessorKey: "aliases",
      accessorFn: (row) => row.aliases || "",
      meta: {
        width: "20%",
      },
      cell: ({ row }) => {
        return (
          <ExpandableText
            className={styles.cellData}
            text={row.original.aliases ?? ""}
          />
        );
      },
      sortingFn: "text",
    },
    {
      header: () => <div className={styles.cellData}>Content</div>,
      accessorKey: "content",
      accessorFn: (row) => row.content,
      meta: {
        width: "45%",
      },
      cell: ({ row }) => {
        return <ExpandableText text={row.original.content ?? ""} />;
      },
    },
    {
      header: ({ column }) => {
        return (
          <Button
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Library
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      accessorKey: "library",
      accessorFn: (row) => (row.firmStandardId ? "Firm" : "Personal"),
      meta: {
        width: "10%",
      },
      cell: ({ row }) => {
        return (
          <div className={styles.cellData}>
            {row.original.firmStandardId ? "Firm" : "Personal"}
          </div>
        );
      },
      sortingFn: "text",
    },
    {
      header: "",
      accessorKey: "actions",
      meta: {
        width: "5%",
      },
      cell: ({ row }) => {
        return (
          <div className="d-flex gap-2">
            <Button
              variant="link"
              onClick={() => {
                setShowAddUpdateTerm(true);
                setDefinitionToEdit(row.original);
              }}
              className="p-0"
            >
              <PencilFill size={12} />
            </Button>
            <Button
              variant="link"
              onClick={() => setDefinitionToDelete(row.original)}
              className="p-0"
            >
              <TrashFill size={12} />
            </Button>
          </div>
        );
      },
    },
  ];

  /* search filtering */
  const filteredDefinitions = useMemo(() => {
    return definitionsQuery.data?.filter(
      (definition) =>
        definition.term?.toLowerCase().includes(search.toLowerCase()) ||
        definition.content?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [definitionsQuery.data, search]);

  /* table react table */
  const table = useReactTable({
    data: filteredDefinitions ?? [],
    getSortedRowModel: getSortedRowModel(),
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      sorting: [{ id: "term", desc: false }],
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  });

  return (
    <Loading isLoading={definitionsQuery.isLoading}>
      {/* Delete Definition Modal */}
      <FormModal
        title={"Are you sure?"}
        cancelText="Cancel"
        confirmText="Yes, delete"
        show={!!definitionToDelete}
        onClose={() => setDefinitionToDelete(undefined)}
        disableConfirmButton={updateDefinitionMutation.isPending}
        confirming={updateDefinitionMutation.isPending}
        onConfirm={() =>
          definitionToDelete &&
          updateDefinitionMutation.mutate(definitionToDelete)
        }
      >
        <div>
          Are you sure you want to remove this Definition? This cannot be
          undone.
        </div>
      </FormModal>

      <AddEditDefinition
        show={showAddUpdateTerm}
        definition={definitionToEdit}
        allDefinitions={definitionsQuery.data ?? []}
        onClose={() => {
          setShowAddUpdateTerm(false);
          setDefinitionToEdit(undefined);
        }}
        onConfirm={() => {}}
      />

      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <SearchInput
            value={search}
            onChange={(value) => setSearch(value)}
            placeholder="Search Definitions"
            size="sm"
            className={styles.searchInput}
          />
          <div className="ms-2">
            <InfoCircle size={20} />
            <Link
              className={styles.placeholderLink}
              to="/tutorials#customizing"
              target="_blank"
            >
              Learn how to customize your content with Placeholders.
            </Link>
          </div>
        </div>
        <div>
          <Button
            size="sm"
            onClick={() => setShowAddUpdateTerm(true)}
            className="px-3"
          >
            + Add New Term
          </Button>
        </div>
      </div>
      <Table className="mt-2">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow className={styles.tableHeaderRow} key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{
                      width: (cell.column.columnDef.meta as { width: string })
                        ?.width, // taken from column meta definition
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <div className={`${styles.cellData} pt-2`}>
              No definitions found
            </div>
          )}
        </TableBody>
      </Table>
      <div className="d-flex justify-content-end">
        <Pagination
          navigate={(page) => table.setPageIndex(page - 1)}
          currentPage={table.getState().pagination.pageIndex + 1}
          totalPages={table.getPageCount()}
          totalItemCount={filteredDefinitions?.length ?? 0}
          itemsPerPage={table.getState().pagination.pageSize}
          showPageCount
          showInput
        />
      </div>
    </Loading>
  );
}
